import { useCallback, useEffect, useState } from 'react';
import { loadSession, saveSession, type Session } from './api.js';
import { Login } from './Login.js';
import { Today } from './Today.js';
import { Log } from './Log.js';
import { Recipes } from './Recipes.js';
import { Library } from './Library.js';
import { Sources } from './Sources.js';
import { Trends } from './Trends.js';
import { Daily } from './Daily.js';
import { Training } from './Training.js';
import { Shopping } from './Shopping.js';
import { Nutrients } from './Nutrients.js';
import { Coach } from './Coach.js';
import { Risk } from './Risk.js';
import { Calendar } from './Calendar.js';
import { Bloods } from './Bloods.js';
import { Chat } from './Chat.js';
import { System } from './System.js';
import { Analysis } from './Analysis.js';
import { AnnouncementBanner } from './Announcements.js';

/**
 * Five primary destinations, not thirteen lowercase tabs.
 *
 * The previous nav listed every screen at the same level, which meant the two
 * you open daily sat beside the one you open twice a year. These groupings are
 * the same five the phone uses, so somebody moving between the two does not have
 * to learn a second map of the same app.
 */
type Section = 'today' | 'log' | 'plan' | 'health' | 'chat';
type Page =
  | 'today' | 'trends' | 'coach'
  | 'log' | 'nutrients' | 'recipes' | 'shopping' | 'training' | 'daily'
  | 'compounds' | 'sources'
  | 'risk' | 'bloods' | 'calendar' | 'analysis'
  | 'chat' | 'system';

const SECTIONS: Array<{ id: Section; label: string; pages: Array<{ id: Page; label: string }> }> = [
  { id: 'today', label: 'Today', pages: [
    { id: 'today', label: 'Today' },
    { id: 'trends', label: 'Trends' },
    { id: 'coach', label: 'Brief' },
  ] },
  { id: 'log', label: 'Log', pages: [
    { id: 'log', label: 'Food' },
    { id: 'nutrients', label: 'Nutrients' },
  ] },
  { id: 'plan', label: 'Plan', pages: [
    { id: 'recipes', label: 'Recipes' },
    { id: 'shopping', label: 'Shopping' },
    { id: 'training', label: 'Training' },
  ] },
  { id: 'health', label: 'Health', pages: [
    { id: 'daily', label: 'Daily inputs' },
    { id: 'risk', label: 'Risk' },
    { id: 'bloods', label: 'Bloods' },
    { id: 'calendar', label: 'Calendar' },
    { id: 'analysis', label: 'Analysis' },
    { id: 'compounds', label: 'Compounds' },
    { id: 'sources', label: 'Sources' },
  ] },
  { id: 'chat', label: 'Chat', pages: [
    { id: 'chat', label: 'Chat' },
  ] },
];

/**
 * The web does not run the setup guide.
 *
 * Accounts are created in the Android app, so the guide lives there too. A
 * half-finished account arriving here gets pointed back at the phone rather
 * than a second, divergent copy of the same six steps.
 */
function FinishInTheApp(): JSX.Element {
  return (
    <div className="card">
      <h2>Finish setting up on your phone</h2>
      <p className="muted small">
        The setup guide is in the Android app. It reads your InBody sheet with the camera, which
        is the part that makes every number here accurate, and that only works on a phone.
      </p>
      <p className="muted small">
        Once it is done, everything appears here automatically.
      </p>
    </div>
  );
}

export function App(): JSX.Element {
  const [session, setSession] = useState<Session | null>(loadSession());
  const [section, setSection] = useState<Section>('today');
  const [page, setPage] = useState<Page>('today');
  // Owner-only. System is operational detail, not a destination.
  const [settingsOpen, setSettingsOpen] = useState(false);

  // The installed app's shortcuts land on /?go=log. Read once, then clean the
  // URL so a refresh does not keep yanking the user back to the same screen.
  useEffect(() => {
    const go = new URLSearchParams(window.location.search).get('go');
    const hit = SECTIONS.find((s) => s.id === go);
    if (hit) {
      setSection(hit.id);
      setPage(hit.pages[0]!.id);
    }
    if (go) window.history.replaceState({}, '', window.location.pathname);
  }, []);

  const current = SECTIONS.find((x) => x.id === section)!;
  const go = (s: Section): void => {
    setSection(s);
    setSettingsOpen(false);
    setPage(SECTIONS.find((x) => x.id === s)!.pages[0]!.id);
  };
  const [nonce, setNonce] = useState(0);
  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  if (!session) return <Login onIn={setSession} />;

  return (
    <main className="shell">
      <header className="topbar">
        <div>
          <h1>Orbit Health</h1>
          <p className="sub">{session.displayName ?? 'Signed in'}</p>
        </div>
        <div className="row">
          <button type="button" className="ghost" onClick={() => { setSettingsOpen(true); setPage('system'); }}>
            Settings
          </button>
          <button type="button" className="ghost" onClick={() => { saveSession(null); setSession(null); }}>
            Sign out
          </button>
        </div>
      </header>

      <AnnouncementBanner />

      {!session.onboardingComplete && <FinishInTheApp />}

      {session.onboardingComplete && (
        <>
          <nav className="tabs">
            {SECTIONS.map((s) => (
              <button key={s.id} type="button"
                      className={!settingsOpen && section === s.id ? 'on' : ''}
                      onClick={() => go(s.id)}>
                {s.label}
              </button>
            ))}
          </nav>
          {/* A second row only when the section has somewhere else to go. */}
          {!settingsOpen && current.pages.length > 1 && (
            <nav className="tabs sub">
              {current.pages.map((p) => (
                <button key={p.id} type="button" className={page === p.id ? 'on' : ''} onClick={() => setPage(p.id)}>
                  {p.label}
                </button>
              ))}
            </nav>
          )}
        </>
      )}

      {session.onboardingComplete && page === 'today' && !settingsOpen && <Today key={nonce} />}
      {session.onboardingComplete && page === 'log' && !settingsOpen && <Log onLogged={refresh} />}
      {session.onboardingComplete && page === 'recipes' && !settingsOpen && <Recipes />}
      {session.onboardingComplete && page === 'nutrients' && !settingsOpen && <Nutrients />}
      {session.onboardingComplete && page === 'shopping' && !settingsOpen && <Shopping />}
      {session.onboardingComplete && page === 'training' && !settingsOpen && <Training />}
      {session.onboardingComplete && page === 'daily' && !settingsOpen && <Daily />}
      {session.onboardingComplete && page === 'trends' && !settingsOpen && <Trends />}
      {session.onboardingComplete && page === 'compounds' && !settingsOpen && <Library />}
      {session.onboardingComplete && page === 'sources' && !settingsOpen && <Sources />}
      {session.onboardingComplete && page === 'coach' && !settingsOpen && <Coach />}
      {session.onboardingComplete && page === 'calendar' && !settingsOpen && <Calendar />}
      {session.onboardingComplete && page === 'risk' && !settingsOpen && <Risk />}
      {session.onboardingComplete && page === 'bloods' && !settingsOpen && <Bloods />}
      {session.onboardingComplete && page === 'analysis' && !settingsOpen && <Analysis />}
      {session.onboardingComplete && page === 'chat' && !settingsOpen && <Chat />}
      {session.onboardingComplete && settingsOpen && <System />}
    </main>
  );
}
